import { Link } from 'react-router-dom';
import { useQuery, useQueries } from '@tanstack/react-query';
import { FileBarChart } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { Spinner, EmptyState, Badge } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/utils';

/** Reports = the most recent completed run per project, presented as a report card. */
export function Reports() {
  const projectsQuery = useQuery({ queryKey: ['projects'], queryFn: () => api.listProjects() });
  const projects = projectsQuery.data?.projects ?? [];

  const runsQueries = useQueries({
    queries: projects.map((p) => ({
      queryKey: ['project-runs', p.id],
      queryFn: () => api.listProjectRuns(p.id),
    })),
  });

  const loading = projectsQuery.isLoading || runsQueries.some((q) => q.isLoading);

  const reports = projects
    .map((p, i) => {
      const runs = (runsQueries[i].data?.runs ?? [])
        .slice()
        .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
      return runs[0] ? { project: p, run: runs[0] } : null;
    })
    .filter((x): x is NonNullable<typeof x> => x !== null);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-text-primary">Reports</h1>
        <p className="mt-1 text-sm text-text-secondary">
          The latest visibility report for each project.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Spinner className="h-7 w-7" />
        </div>
      ) : reports.length === 0 ? (
        <EmptyState
          icon={<FileBarChart size={28} />}
          title="No reports yet"
          description="Run an analysis to generate your first report."
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {reports.map(({ project, run }) => (
            <Link key={run.runId} to={`/app/results/${run.runId}`}>
              <Card className="h-full transition hover:border-accent-blue/40">
                <CardContent className="pt-5">
                  <div className="flex items-start justify-between">
                    <h3 className="font-semibold text-text-primary">{project.name}</h3>
                    <Badge tone="blue">{run.metrics?.visibilityScore.toFixed(0) ?? '—'}</Badge>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                    <Stat label="Mentions" value={run.metrics?.totalMentions ?? 0} />
                    <Stat label="Share of voice" value={`${run.metrics?.shareOfVoice ?? 0}%`} />
                  </div>
                  <p className="mt-4 text-xs text-text-muted">{formatDate(run.createdAt)}</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg bg-bg-raised/60 p-3">
      <p className="text-xs text-text-muted">{label}</p>
      <p className="mt-0.5 font-semibold text-text-primary">{value}</p>
    </div>
  );
}
